import React from "react"
import { AgendaRow } from "sharedTypes"
import { AGENDA_ROW_TYPES } from "utilities/enums"
import * as Yup from "yup"
import { Text } from "@chakra-ui/react"
import ModalForm from "components/dialogs/ReactHookForm/ModalForm"
import TextField from "components/ReactHookForm/TextField"
import AgendaDateTimeField from "components/ReactHookForm/AgendaDateTimeField"
import RichTextField, { RichTextFieldType } from "components/ReactHookForm/RichTextField"
import * as api from "api/websites"
import { addAgendaRow, removeAgendaRow, updateAgendaRow } from "context/actions"
import { useQueryClient } from "react-query"
import { successToast } from "utilities/toasts"
import { eventStartDateToAgendaUTC } from "utilities/strings"
import { AgendaFormValues } from "pages/EventShow/WebPage/types"

interface Props {
  eventId: string
  onHide: () => void
  onChange?: (values: AgendaFormValues) => void
  agendaGroup?: AgendaRow
  eventStartDate?: string
  eventTimezone?: string
  agendaRows?: AgendaRow[]
}

const AddAgendaGroup = ({
  eventId, onHide, onChange, agendaGroup, eventStartDate, eventTimezone, agendaRows = [],
}: Props) => {
  const queryClient = useQueryClient()

  const create = (data) => api.postAgendaRow(eventId, {
    ...data,
    type: AGENDA_ROW_TYPES.heading,
  })
    .then((response) => {
      addAgendaRow(queryClient, eventId, response.data)
      successToast({ title: "Agenda updated" })
    })

  const update = (data) => api.putAgendaRow(eventId, data.id, data)
    .then((response) => {
      updateAgendaRow(queryClient, eventId, response.data)
      successToast({ title: "Agenda updated" })
    })

  const onDelete = () => {
    if (!agendaGroup) return Promise.resolve()

    return api.deleteAgendaRow(eventId, agendaGroup.id)
      .then(() => {
        removeAgendaRow(queryClient, eventId, agendaGroup.id)
        successToast({ title: "Agenda group removed" })
        onHide()
      })
  }

  const onSubmit = (values) => {
    if (agendaGroup) {
      return update({ ...values, id: agendaGroup.id, type: AGENDA_ROW_TYPES.heading }).then(() => {
        onHide()
      })
    }

    return create(values).then(() => {
      onHide()
    })
  }

  const validationSchema = Yup.object().shape({
    title: Yup.string().required("Title is required."),
    description: Yup.string().nullable(),
    startsAt: Yup.date()
      .typeError("Start time is required.")
      .required("Start time is required."),
  })

  const defaultStartsAt = (() => {
    if (agendaGroup) return undefined
    if (agendaRows.length === 0) {
      if (!eventStartDate) return new Date().toISOString()
      const utc = eventTimezone
        ? eventStartDateToAgendaUTC(eventStartDate, eventTimezone)
        : new Date(eventStartDate).toISOString()
      return utc ?? new Date().toISOString()
    }
    const sorted = [...agendaRows].sort((a, b) => {
      const aEnd = a.endsAt || a.startsAt
      const bEnd = b.endsAt || b.startsAt
      return new Date(aEnd).getTime() - new Date(bEnd).getTime()
    })
    const last = sorted[sorted.length - 1]
    return last.endsAt || last.startsAt
  })()

  const initialValues = agendaGroup ? { ...agendaGroup } : {
    title: "",
    description: "",
    startsAt: defaultStartsAt ?? "",
  }

  return (
    <ModalForm
      key={agendaGroup?.id || "new"}
      title={`${agendaGroup ? "Edit" : "Add"} Agenda Group`}
      onClose={onHide}
      initialValues={initialValues}
      validationSchema={validationSchema}
      mode="onBlur"
      onSubmit={onSubmit}
      onDanger={agendaGroup ? onDelete : null}
      dangerProps={agendaGroup ? { type: "agenda group", name: agendaGroup.title } : undefined}
      disableSubmitWhenNotDirty
      onChange={onChange}
    >
      {({
        control, errors,
      }) => {
        const startsAtMessage = errors.startsAt?.message as string | undefined
        const titleMessage = errors.title?.message as string | undefined

        return (
          <>
            {!agendaGroup && (
              <Text mb={8} fontSize="md">
                Create a section header to group agenda entries, such as separating different days
                {" "}
                or program sections. The start time is used only to determine its position
                {" "}
                and is not shown on the page.
              </Text>
            )}
            <AgendaDateTimeField
              showTimeSelect
              utc
              name="startsAt"
              control={control}
              label="Start Time"
              variant="outline"
              error={!!startsAtMessage}
              errorText={startsAtMessage}
            />
            <TextField
              name="title"
              control={control}
              label="Title"
              variant="outline"
              error={!!titleMessage}
              errorText={titleMessage}
            />
            <RichTextField
              name="description"
              control={control}
              label="Description"
              type={RichTextFieldType.MINIMAL}
            />
          </>
        )
      }}
    </ModalForm>
  )
}

export default AddAgendaGroup
